import { useQuery } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";

/**
 * Who the church's texts come from (`messaging_sender`, ADR 0072): whether pilot texting is on, the name every text
 * ends with ("<name>: Reply STOP to opt out"), the number they go out from, and whether that sender is live. While
 * pilot texting is on, the name and number are DCX Logic's registered pilot sender's, never the church's own.
 */
export interface SmsSender {
  readonly pilot: boolean;
  readonly senderName: string;
  readonly from: string | null;
  readonly live: boolean;
}

const text = (value: unknown): string | null => (typeof value === "string" ? value : null);

/** Reads the church's text sender; needs communications.read or integrations.manage (the database refuses others). */
export function useSmsSender(enabled = true) {
  const supabase = useSupabase();
  const tenant = useTenant();
  const tenantId = tenant.status === "resolved" ? tenant.tenant.tenantId : null;
  return useQuery({
    queryKey: ["sms-sender", tenantId],
    enabled: enabled && tenantId !== null,
    staleTime: 60_000,
    queryFn: async (): Promise<SmsSender> => {
      const { data, error } = await supabase.rpc("messaging_sender", {
        p_tenant_id: tenantId ?? "",
      });
      if (error) throw error;
      const d = (data ?? {}) as Record<string, unknown>;
      return {
        pilot: d["pilot"] === true,
        senderName: text(d["senderName"]) ?? "",
        from: text(d["from"]),
        live: d["live"] === true,
      };
    },
  });
}
